import React, { useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import api from '../../services/api';
import { mensagemAlerta } from '../../common/utils';

const CAMPOS_POR_FORMA = {
    pix: ['pagamento_chave_pix'],
    'cartao-de-credito': ['pagamento_url_cartao_credito'],
    'transferencia-bancaria': ['pagamento_banco', 'pagamento_agencia', 'pagamento_tipo_conta', 'pagamento_conta', 'pagamento_titular'],
};

const getUltimoDiaUtilMesAtual = () => {
    const hoje = new Date();
    const data = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    while (data.getDay() === 0 || data.getDay() === 6) {
        data.setDate(data.getDate() - 1);
    }
    return data.toISOString().split('T')[0];
};

function OSAvulsaPagamentoForm({ faturamentoId, dadosFaturamento, formasPagamento, onSalvo }) {
    const [salvando, setSalvando] = useState(false);
    const [pagamentoFormaId, setPagamentoFormaId] = useState(dadosFaturamento.pagamento_forma_id || '');
    const [dadosPagamento, setDadosPagamento] = useState({
        pagamento_chave_pix: dadosFaturamento.pagamento_chave_pix || '',
        pagamento_url_cartao_credito: dadosFaturamento.pagamento_url_cartao_credito || '',
        pagamento_banco: dadosFaturamento.pagamento_banco || '',
        pagamento_agencia: dadosFaturamento.pagamento_agencia || '',
        pagamento_tipo_conta: dadosFaturamento.pagamento_tipo_conta || 'corrente',
        pagamento_conta: dadosFaturamento.pagamento_conta || '',
        pagamento_titular: dadosFaturamento.pagamento_titular || '',
    });
    const [dataPagamento, setDataPagamento] = useState(
        dadosFaturamento.data_pagamento ? String(dadosFaturamento.data_pagamento).substring(0, 16) : ''
    );
    const [notaFiscalNumero, setNotaFiscalNumero] = useState(dadosFaturamento.nota_fiscal_numero || '');
    const [dataEmissao, setDataEmissao] = useState(dadosFaturamento.data_emissao || '');
    const [dataVencimento, setDataVencimento] = useState(dadosFaturamento.data_vencimento || '');
    const [arquivoComprovante, setArquivoComprovante] = useState(null);
    const [arquivoNota, setArquivoNota] = useState(null);
    const [arquivoDadosPagamento, setArquivoDadosPagamento] = useState(null);

    const formaSelecionada = (formasPagamento || []).find(forma => String(forma.id) === String(pagamentoFormaId || '')) || null;
    const slugFormaSelecionada = formaSelecionada?.slug || '';

    const handleArquivoPdf = (event, setter) => {
        const file = event.target.files?.[0] || null;

        if (file && file.type !== 'application/pdf') {
            mensagemAlerta('Selecione um arquivo PDF válido.', 'warning');
            event.target.value = '';
            return;
        }

        setter(file);
    };

    const salvar = async () => {
        const formData = new FormData();
        formData.append('tipo_faturamento', 'servico');

        if (pagamentoFormaId) {
            formData.append('pagamento_forma_id', pagamentoFormaId);
        }

        if (slugFormaSelecionada && CAMPOS_POR_FORMA[slugFormaSelecionada]) {
            CAMPOS_POR_FORMA[slugFormaSelecionada].forEach(campo => {
                if (dadosPagamento[campo]) {
                    formData.append(campo, dadosPagamento[campo]);
                }
            });
        }

        if (arquivoComprovante) {
            formData.append('pagamento_comprovante', arquivoComprovante);
        }
        if (dataPagamento) {
            formData.append('data_pagamento', dataPagamento);
        }
        if (arquivoDadosPagamento) {
            formData.append('arquivo_dados_pagamento', arquivoDadosPagamento);
        }
        if (arquivoNota) {
            formData.append('nota', arquivoNota);
        }
        if (notaFiscalNumero) {
            formData.append('nota_fiscal_numero', notaFiscalNumero);
        }
        if (dataEmissao) {
            formData.append('data_emissao', dataEmissao);
        }
        if (dataVencimento) {
            formData.append('data_vencimento', dataVencimento);
        } else if (arquivoNota) {
            formData.append('data_vencimento', getUltimoDiaUtilMesAtual());
        }

        // marca a OS como paga automaticamente quando já existe comprovante + data de pagamento
        const temComprovante = !!(arquivoComprovante || dadosFaturamento.pagamento_comprovante);
        const temDataPagamento = !!dataPagamento;
        if (temComprovante && temDataPagamento) {
            formData.append('pago', true);
        }

        setSalvando(true);

        try {
            await api.put(`/faturamento/${faturamentoId}/`, formData);
            mensagemAlerta('Dados salvos na Ordem de Serviço com sucesso!', 'success');
            setArquivoComprovante(null);
            setArquivoNota(null);
            setArquivoDadosPagamento(null);
            if (onSalvo) {
                onSalvo();
            }
        } catch (error) {
            let erro_msg = 'Erro ao salvar os dados da Ordem de Serviço.';

            if (error.response?.data) {
                const data = error.response.data;
                const messages = [];

                Object.keys(data).forEach((key) => {
                    if (Array.isArray(data[key])) {
                        messages.push(...data[key]);
                    } else if (typeof data[key] === 'string') {
                        messages.push(data[key]);
                    }
                });

                if (messages.length > 0) {
                    erro_msg = messages.join('\n');
                }
            }

            mensagemAlerta(erro_msg, 'error');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Card className="mt-3 border">
            <Card.Header className="bg-light">
                <strong>Pagamento e Nota Fiscal da Ordem de Serviço</strong>
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col sm={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Forma de Pagamento</Form.Label>
                            <Form.Control as="select" value={pagamentoFormaId} onChange={event => setPagamentoFormaId(event.target.value)}>
                                <option value="">Selecione</option>
                                {(formasPagamento || []).map(forma => (
                                    <option key={forma.id} value={forma.id}>{forma.nome}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col sm={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Data do Pagamento</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={dataPagamento}
                                onChange={event => setDataPagamento(event.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {slugFormaSelecionada && (
                    <Card className="mb-3 border bg-white">
                        <Card.Body>
                            <h6 className="mb-3">Dados da Forma de Pagamento</h6>

                            {slugFormaSelecionada === 'pix' && (
                                <Form.Group className="mb-0">
                                    <Form.Label>Chave PIX</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={dadosPagamento.pagamento_chave_pix}
                                        onChange={event => setDadosPagamento({ ...dadosPagamento, pagamento_chave_pix: event.target.value })}
                                    />
                                </Form.Group>
                            )}

                            {slugFormaSelecionada === 'cartao-de-credito' && (
                                <Form.Group className="mb-0">
                                    <Form.Label>Link de Pagamento (Cartão de Crédito)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={dadosPagamento.pagamento_url_cartao_credito}
                                        onChange={event => setDadosPagamento({ ...dadosPagamento, pagamento_url_cartao_credito: event.target.value })}
                                    />
                                </Form.Group>
                            )}

                            {slugFormaSelecionada === 'transferencia-bancaria' && (
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Banco</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={dadosPagamento.pagamento_banco}
                                                onChange={event => setDadosPagamento({ ...dadosPagamento, pagamento_banco: event.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Agência</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={dadosPagamento.pagamento_agencia}
                                                onChange={event => setDadosPagamento({ ...dadosPagamento, pagamento_agencia: event.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Tipo da Conta</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={dadosPagamento.pagamento_tipo_conta}
                                                onChange={event => setDadosPagamento({ ...dadosPagamento, pagamento_tipo_conta: event.target.value })}
                                            >
                                                <option value="corrente">Conta Corrente</option>
                                                <option value="poupanca">Poupança</option>
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Conta</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={dadosPagamento.pagamento_conta}
                                                onChange={event => setDadosPagamento({ ...dadosPagamento, pagamento_conta: event.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group className="mb-0">
                                            <Form.Label>Titular (CPF/CNPJ)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={dadosPagamento.pagamento_titular}
                                                onChange={event => setDadosPagamento({ ...dadosPagamento, pagamento_titular: event.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                )}

                <Row>
                    <Col sm={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Dados para Pagamento (PDF)</Form.Label>
                            <Form.Control type="file" accept="application/pdf" onChange={event => handleArquivoPdf(event, setArquivoDadosPagamento)} />
                            {dadosFaturamento.arquivo_dados_pagamento && (
                                <a className="btn btn-outline-secondary btn-sm mt-2" href={dadosFaturamento.arquivo_dados_pagamento} target="_blank" rel="noreferrer">
                                    Visualizar
                                </a>
                            )}
                        </Form.Group>
                    </Col>
                    <Col sm={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Comprovante de Pagamento (PDF)</Form.Label>
                            <Form.Control type="file" accept="application/pdf" onChange={event => handleArquivoPdf(event, setArquivoComprovante)} />
                            {dadosFaturamento.pagamento_comprovante && (
                                <a className="btn btn-outline-secondary btn-sm mt-2" href={dadosFaturamento.pagamento_comprovante} target="_blank" rel="noreferrer">
                                    Visualizar
                                </a>
                            )}
                        </Form.Group>
                    </Col>
                    <Col sm={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nota Fiscal (PDF)</Form.Label>
                            <Form.Control type="file" accept="application/pdf" onChange={event => handleArquivoPdf(event, setArquivoNota)} />
                            {dadosFaturamento.nota && (
                                <a className="btn btn-outline-secondary btn-sm mt-2" href={dadosFaturamento.nota} target="_blank" rel="noreferrer">
                                    Visualizar
                                </a>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col sm={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Número da Nota Fiscal</Form.Label>
                            <Form.Control type="text" value={notaFiscalNumero} onChange={event => setNotaFiscalNumero(event.target.value)} />
                        </Form.Group>
                    </Col>
                    <Col sm={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Data de Emissão</Form.Label>
                            <Form.Control type="date" value={dataEmissao} onChange={event => setDataEmissao(event.target.value)} />
                        </Form.Group>
                    </Col>
                    <Col sm={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Data de Vencimento</Form.Label>
                            <Form.Control
                                type="date"
                                value={dataVencimento || getUltimoDiaUtilMesAtual()}
                                onChange={event => setDataVencimento(event.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Alert variant="info" className="py-2 small mb-3">
                    Anexar o comprovante de pagamento junto com a data do pagamento marca a Ordem de
                    Serviço como paga automaticamente.
                </Alert>

                <Button variant="primary" size="sm" onClick={salvar} disabled={salvando}>
                    {salvando ? 'Salvando...' : 'Salvar na Ordem de Serviço'}
                </Button>
            </Card.Body>
        </Card>
    );
}

export default OSAvulsaPagamentoForm;
